/**
 * Lecture des réponses d'apiCallWithAuth pour les services qui lèvent une erreur lisible plutôt
 * que de rendre la réponse brute (catalogues sanitaires, actions personnalisées, grilles,
 * évènements de structure). Une seule copie : une correction sert à tous.
 */

/** Message de repli quand la réponse ne porte aucun message exploitable. */
const DEFAULT_UNKNOWN_ERROR = "Erreur inconnue";

/**
 * Erreur dont le message est écrit pour l'opérateur : message du serveur lu par `unwrap`, ou texte
 * traduit (session expirée, levée par apiCallWithAuth). Seules celles-ci sont affichées telles
 * quelles par operatorErrorMessage ; toute autre erreur (TypeError, erreur d'un développeur…)
 * porte un texte technique et cède la place au repli traduit (revue sprint 2).
 */
export class ReadableError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ReadableError";
    }
}

export interface IUnwrapOptions {
    /**
     * Message de repli, lu au moment de l'erreur (une traduction suit ainsi la langue courante).
     * « Erreur inconnue » par défaut.
     */
    fallbackMessage?: () => string;
    /**
     * Lire aussi la réponse d'une erreur LEVÉE par axios (404, 500…), pour garder le message du
     * serveur plutôt que « Request failed with status code 500 ». Sans réponse (réseau, session
     * expirée), l'erreur d'origine est relancée telle quelle : elle dit déjà pourquoi.
     */
    readThrownResponse?: boolean;
}

/**
 * Extrait un message lisible d'une réponse d'erreur au format DataOperationResult (voir
 * src/models/api/dataOperationError.ts) ou ProblemDetails (`title`) ; une chaîne est rendue telle
 * quelle, sauf une page HTML (proxy en 502, page d'erreur de développement) : ce n'est pas un
 * message, c'est le repli. Le garde est ici, et non chez un seul appelant, pour couvrir tous les
 * chemins — unwrap avec readThrownResponse la rendait telle quelle à l'écran (revue sprint 2).
 */
export function extractErrorMessage(data: unknown, fallbackMessage: () => string = () => DEFAULT_UNKNOWN_ERROR): string {
    if (!data) return fallbackMessage();
    if (typeof data === "string") return data.trimStart().startsWith("<") ? fallbackMessage() : data;
    const body = data as { errors?: unknown; title?: unknown };
    if (body.errors && typeof body.errors === "object") {
        const messages = Object.values(body.errors as Record<string, unknown>).filter((v) => !!v);
        if (messages.length > 0) return messages.join(" — ");
    }
    if (typeof body.title === "string") return body.title;
    return fallbackMessage();
}

/**
 * Le message à montrer à l'opérateur pour une erreur attrapée par un écran : le message métier
 * quand il existe, sinon `fallbackMessage` (traduit), JAMAIS le texte technique d'axios
 * (« Request failed with status code 500 », « Network Error ») qui ne dit rien à l'opérateur.
 *
 * - Erreur levée par axios AVEC réponse : le message du serveur y est lu (DataOperationResult,
 *   ProblemDetails, chaîne) par extractErrorMessage ; une page HTML (proxy, page d'erreur de
 *   développement) n'en est pas un.
 * - Erreur axios SANS réponse (coupure réseau, délai dépassé) : le repli.
 * - ReadableError — levée par `unwrap`, ou « session expirée » traduite par apiCallWithAuth — :
 *   son message.
 * - Toute autre erreur (TypeError, erreur levée par un développeur) : le repli ; son texte est
 *   technique.
 */
export function operatorErrorMessage(error: unknown, fallbackMessage: () => string): string {
    const e = error as { response?: { data?: unknown }; isAxiosError?: boolean } | null | undefined;
    if (e?.response) return extractErrorMessage(e.response.data, fallbackMessage);
    if (e?.isAxiosError) return fallbackMessage();
    return error instanceof ReadableError && error.message ? error.message : fallbackMessage();
}

/** Vrai pour un statut HTTP hors 2xx ; une réponse sans statut est prise pour un succès. */
export function isErrorStatus(result: { status?: number } | null | undefined): boolean {
    return result?.status !== undefined && (result.status < 200 || result.status >= 300);
}

/**
 * Résout un appel apiCallWithAuth en donnée typée, ou lève une erreur lisible.
 * apiCallWithAuth ne lève PAS sur un 400 (il renvoie la réponse telle quelle, voir
 * src/helper/apiManager.ts) : il faut donc vérifier le statut explicitement, faute de quoi un
 * DataOperationResult d'erreur serait pris pour la donnée sauvegardée.
 */
export async function unwrap<T>(promise: Promise<any>, options: IUnwrapOptions = {}): Promise<T> {
    let result: any;
    try {
        result = await promise;
    } catch (error: unknown) {
        if (!options.readThrownResponse) throw error;
        const response = (error as { response?: { data?: unknown } } | null)?.response;
        if (!response) throw error;
        throw new ReadableError(extractErrorMessage(response.data, options.fallbackMessage));
    }
    if (isErrorStatus(result)) {
        throw new ReadableError(extractErrorMessage(result.data, options.fallbackMessage));
    }
    return result?.data as T;
}
